import { useEffect, useState } from "react";
import {
  Building2, MapPin, User, Save, Edit3, ChevronDown, ChevronRight,
  Plus, Trash2, Phone, Mail, Star,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import PlaceInput from "../PlaceInput";
import { toast } from "sonner";

/**
 * PortalProfileEditorBlock — self-service company-profile editor.
 *
 * Clients can edit their company details (name, phone, email, billing
 * address, VAT/reg numbers, website), plus manage their contacts and
 * sites (CRUD). Uses new `/portal/profile` + `/portal/contacts` +
 * `/portal/sites` routes that enforce `role=client` on their own
 * customer only. In manager preview mode, the block is rendered
 * read-only so staff can see exactly what the client sees.
 */
const FIELDS = [
  { key: "name", label: "Company / Contact name", type: "text", full: true },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "email", label: "Email", type: "email" },
  { key: "website", label: "Website", type: "url" },
  { key: "vat_number", label: "VAT number", type: "text" },
  { key: "registration_number", label: "Registration #", type: "text" },
  { key: "billing_address", label: "Billing address", type: "text", full: true },
  { key: "billing_city", label: "City", type: "text" },
  { key: "billing_postal_code", label: "Postal code", type: "text" },
  { key: "billing_country", label: "Country", type: "text" },
];

export default function PortalProfileEditorBlock({ customer, canEdit = false, onChanged, isPreview = false }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => pluck(customer));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const [contacts, setContacts] = useState([]);
  const [sites, setSites] = useState([]);
  const [loadingKids, setLoadingKids] = useState(false);

  useEffect(() => { setForm(pluck(customer)); }, [customer]);

  useEffect(() => {
    if (!open) return;
    loadKids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customer?.id]);

  async function loadKids() {
    if (!customer?.id) return;
    setLoadingKids(true);
    try {
      const [ct, si] = await Promise.all([
        api.get(`/portal/contacts?customer_id=${customer.id}`).catch(() => ({ data: [] })),
        api.get(`/portal/sites?customer_id=${customer.id}`).catch(() => ({ data: [] })),
      ]);
      setContacts(ct.data || []);
      setSites(si.data || []);
    } finally {
      setLoadingKids(false);
    }
  }

  const save = async () => {
    if (isPreview) return;
    setBusy(true);
    setErr(""); setOk("");
    try {
      await api.patch("/portal/profile", form);
      setOk("Saved.");
      setEditing(false);
      onChanged?.();
      setTimeout(() => setOk(""), 2500);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save profile"));
    } finally {
      setBusy(false);
    }
  };

  const count = (contacts?.length || 0) + (sites?.length || 0);

  return (
    <section className="hv-card p-4" data-testid="portal-profile-editor">
      <header
        className="flex items-center justify-between cursor-pointer gap-2"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-[#647087]" /> : <ChevronRight className="w-4 h-4 text-[#647087]" />}
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-[#10B981]/15 border border-[#10B981]/40 text-[#10B981] shrink-0">
            <Building2 className="w-4 h-4" />
          </div>
          <h3 className="font-display text-base font-bold truncate">My company details</h3>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#10B981]/40 text-[#10B981] bg-[#10B981]/5">
            {count ? `${contacts.length} contact${contacts.length === 1 ? "" : "s"} · ${sites.length} site${sites.length === 1 ? "" : "s"}` : "tap to edit"}
          </span>
          {isPreview && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400/30 text-amber-300 bg-amber-400/5">
              Preview · read-only
            </span>
          )}
        </div>
      </header>

      {open && (
        <div className="mt-3 space-y-4" data-testid="portal-profile-panel">
          {/* Top-level profile */}
          <div className="border border-white/10 rounded-md p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs uppercase tracking-wider text-[#a0aabf] font-bold">Profile</div>
              {canEdit && !editing && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid="portal-profile-edit"
                ><Edit3 className="w-3 h-3" /> Edit</button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                  <label className="text-[10px] uppercase tracking-wider text-[#647087]">{f.label}</label>
                  {editing ? (
                    <input
                      type={f.type}
                      value={form[f.key] || ""}
                      onChange={(e) => setForm((d) => ({ ...d, [f.key]: e.target.value }))}
                      className="hv-input mt-0.5 text-sm"
                      data-testid={`portal-profile-${f.key}`}
                    />
                  ) : (
                    <div className="text-sm mt-0.5 text-white truncate" data-testid={`portal-profile-${f.key}-display`}>
                      {form[f.key] || <span className="text-[#647087] italic">—</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {editing && (
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={save}
                  disabled={busy}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="portal-profile-save"
                ><Save className="w-3 h-3" /> {busy ? "Saving…" : "Save"}</button>
                <button
                  onClick={() => { setEditing(false); setForm(pluck(customer)); setErr(""); }}
                  className="hv-btn hv-btn-ghost text-xs"
                >Cancel</button>
                {ok && <span className="text-[10px] text-emerald-300">{ok}</span>}
                {err && <span className="text-[10px] text-rose-300">{err}</span>}
              </div>
            )}
          </div>

          {/* Contacts */}
          <SubList
            icon={User}
            label="Contacts"
            accent="#A78BFA"
            items={contacts}
            fields={[
              { key: "name", label: "Name", required: true },
              { key: "role", label: "Role / Job title" },
              { key: "email", label: "Email", type: "email" },
              { key: "phone", label: "Phone", type: "tel" },
            ]}
            flagKey="is_primary"
            flagIcon={Star}
            canEdit={canEdit && !isPreview}
            endpoint="/portal/contacts"
            onChanged={loadKids}
            testPrefix="portal-contact"
          />

          {/* Sites */}
          <SubList
            icon={MapPin}
            label="Sites"
            accent="#1FB6C1"
            items={sites}
            fields={[
              { key: "name", label: "Site name", required: true },
              { key: "address", label: "Address", full: true, required: true, type: "place" },
              { key: "notes", label: "Notes", full: true },
            ]}
            canEdit={canEdit && !isPreview}
            endpoint="/portal/sites"
            onChanged={loadKids}
            testPrefix="portal-site"
          />

          {loadingKids && <div className="text-xs text-[#647087]">Loading…</div>}

          {/* Iter 418.y (Dirk, 2026-02-28) — Personal email opt-out.
              Layered on TOP of the customer-level safety-net flag. Even
              when the customer is opted-in, individual portal users can
              switch their notifications off here. */}
          {!isPreview && (
            <EmailOptOutBlock />
          )}
        </div>
      )}
    </section>
  );
}

function EmailOptOutBlock() {
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/portal/me");
        if (live && data) {
          // Missing / null ⇒ opted-in (default true).
          setEnabled(data.email_notifications_enabled !== false);
        }
      } catch { /* silent */ }
      if (live) setLoaded(true);
    })();
    return () => { live = false; };
  }, []);
  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = !enabled;
      await api.patch("/portal/me/email-notifications", { enabled: next });
      setEnabled(next);
      toast.success(next ? "Emails: ON" : "Emails: OFF");
    } catch (e) {
      toast.error("Could not update — try again");
    } finally {
      setBusy(false);
    }
  };
  if (!loaded) return null;
  return (
    <div className="border border-white/10 rounded-md p-3" data-testid="portal-email-opt-out">
      <div className="text-xs uppercase tracking-wider text-[#a0aabf] font-bold mb-2">Email preferences</div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={`hv-btn ${enabled ? "hv-btn-primary" : "hv-btn-ghost"} text-xs`}
        data-testid="portal-email-opt-out-toggle"
      >
        <Mail className="w-3 h-3" /> Get emails from HidroVerse: {enabled ? "ON" : "OFF"}
      </button>
      <p className="mt-2 text-[10px] text-[#647087]">
        When ON, you&apos;ll receive an email whenever a new job card, quote,
        sales order, invoice, credit note, statement, or document is
        published to your portal. Turn OFF to stop these — you can still
        view everything in the portal.
      </p>
    </div>
  );
}

function pluck(c) {
  if (!c) return {};
  const out = {};
  FIELDS.forEach((f) => { out[f.key] = c[f.key] || ""; });
  return out;
}

function SubList({ icon: Icon, label, accent, items, fields, flagKey, flagIcon: FlagIcon, canEdit, endpoint, onChanged, testPrefix }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const startAdd = () => { setDraft({}); setAdding(true); setEditingId(null); setErr(""); };
  const startEdit = (it) => { setDraft({ ...it }); setEditingId(it.id); setAdding(false); setErr(""); };
  const cancel = () => { setDraft({}); setEditingId(null); setAdding(false); setErr(""); };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const req = fields.find((f) => f.required);
      if (req && !draft[req.key]) { setErr(`${req.label} is required.`); setBusy(false); return; }
      if (editingId) await api.patch(`${endpoint}/${editingId}`, draft);
      else await api.post(endpoint, draft);
      cancel();
      onChanged?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`${endpoint}/${id}`);
      onChanged?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not delete"));
    } finally { setBusy(false); }
  };

  const form = (
    <div className="border border-white/10 rounded-md p-3 space-y-2 bg-white/[0.02]" data-testid={`${testPrefix}-form`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {fields.map((f) => (
          <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">{f.label}{f.required && " *"}</label>
            {f.type === "place" ? (
              // [Iter 290zp.13y] Address field uses the same PlaceInput
              // CRM uses — Nominatim autocomplete, "use my current
              // location", and map-pin selection. No new API keys.
              // PlaceInput emits { address, lat, lng, place_id, source }.
              <PlaceInput
                value={draft[f.key] || ""}
                onChange={(picked) => {
                  if (!picked) {
                    setDraft((d) => ({ ...d, [f.key]: "", lat: null, lng: null, place_id: null, address_source: null }));
                    return;
                  }
                  setDraft((d) => ({
                    ...d,
                    [f.key]: picked.address || "",
                    lat: picked.lat ?? null,
                    lng: picked.lng ?? null,
                    place_id: picked.place_id ?? null,
                    address_source: picked.source ?? null,
                  }));
                }}
                placeholder="Search address, or pick on map…"
                testid={`${testPrefix}-field-${f.key}`}
                allowCurrentLocation
                allowMapPin
              />
            ) : (
              <input
                type={f.type || "text"}
                value={draft[f.key] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                className="hv-input mt-0.5 text-sm"
                data-testid={`${testPrefix}-field-${f.key}`}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={busy} className="hv-btn hv-btn-primary text-xs" data-testid={`${testPrefix}-save`}>
          <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save"}
        </button>
        <button onClick={cancel} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
        {err && <span className="text-[10px] text-rose-300">{err}</span>}
      </div>
    </div>
  );

  return (
    <div className="border border-white/10 rounded-md p-3 space-y-2" data-testid={`${testPrefix}-block`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: `${accent}1f`, border: `1px solid ${accent}55`, color: accent }}>
            <Icon className="w-3 h-3" />
          </div>
          <div className="text-xs uppercase tracking-wider font-bold" style={{ color: accent }}>
            {label}
          </div>
          <span className="text-[10px] font-mono text-[#647087]">({items.length})</span>
        </div>
        {canEdit && !adding && !editingId && (
          <button
            type="button"
            onClick={startAdd}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid={`${testPrefix}-add-btn`}
          ><Plus className="w-3 h-3" /> Add</button>
        )}
      </div>

      {adding && form}

      <div className="space-y-1.5">
        {items.length === 0 && !adding && (
          <div className="text-xs text-[#647087] italic py-1.5">No {label.toLowerCase()} yet.</div>
        )}
        {items.map((it) => (
          editingId === it.id ? (
            <div key={it.id}>{form}</div>
          ) : (
            <div
              key={it.id}
              className="flex items-center gap-2 border border-white/5 rounded px-2.5 py-1.5 text-xs bg-white/[0.02]"
              data-testid={`${testPrefix}-row-${it.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate flex items-center gap-1.5">
                  {it.name}
                  {flagKey && it[flagKey] && FlagIcon && (
                    <FlagIcon className="w-3 h-3 text-amber-300 fill-amber-300" />
                  )}
                </div>
                <div className="text-[10px] text-[#647087] truncate">
                  {fields.slice(1).map((f) => it[f.key]).filter(Boolean).join(" · ") || "—"}
                </div>
              </div>
              {canEdit && (
                <>
                  <button onClick={() => startEdit(it)} className="hv-btn hv-btn-ghost text-[10px]" data-testid={`${testPrefix}-edit-${it.id}`}>
                    <Edit3 className="w-3 h-3" />
                  </button>
                  <button onClick={() => remove(it.id)} className="hv-btn hv-btn-ghost text-[10px] text-rose-300 hover:!text-rose-200" data-testid={`${testPrefix}-del-${it.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>
          )
        ))}
      </div>
    </div>
  );
}
